import { FC, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
  Field,
  Input,
  Dropdown,
  Option
} from "@fluentui/react-components";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Question, TenderingIndexing, TenderingPart } from "../../../models/tendering";
import { getTodayKey, toDateKey } from "../../../utils/tendering-utils";

interface CreateIndexingProps {
  open: boolean;
  parts: TenderingPart[];
  onClose: () => void;
  onSave: (indexing: Partial<TenderingIndexing>) => void;
  editingIndexing?: TenderingIndexing | null;
}

interface IndexingFormData {
  indexingName: string;
  description: string;
  startDate: string;
  endDate: string;
  indexingDate: string;
  index: string;
  prices: Question[];
}

const emptyForm: IndexingFormData = {
  indexingName: "",
  description: "",
  startDate: "",
  endDate: "",
  indexingDate: "",
  index: "1.000000",
  prices: []
};

const CreateIndexing: FC<CreateIndexingProps> = ({ open, parts, onClose, onSave, editingIndexing }) => {
  const { control, handleSubmit, reset, trigger, getValues, formState: { errors, isValid } } = useForm<IndexingFormData>({
    defaultValues: emptyForm,
    mode: "onChange",
  });

  const prices = parts
    .filter((tp: TenderingPart) => tp.evaluated)
    .flatMap(part => part.questions || [])
    .filter((q: Question) => q.questionType === "Prisruta");

  useEffect(() => {
    if (!open) return;
    if (editingIndexing) {
      reset({
        indexingName: editingIndexing.indexingName || "",
        description: editingIndexing.indexingDescription || "",
        startDate: toDateKey(editingIndexing.startDate),
        endDate: toDateKey(editingIndexing.endDate),
        indexingDate: toDateKey(editingIndexing.indexingDate),
        index: editingIndexing.index ? editingIndexing.index.toString() : "1.000000",
        prices: editingIndexing.prices || []
      });
    } else {
      reset(emptyForm);
    }
  }, [open, editingIndexing, reset]);

  const revalidateDates = () => {
    const dateFields = (["startDate", "endDate", "indexingDate"] as const).filter(name => getValues(name));
    trigger([...dateFields]);
  };

  const onSubmit = (data: IndexingFormData) => {
    const newIndexing: Partial<TenderingIndexing> = {
      indexingName: data.indexingName,
      indexingDescription: data.description,
      startDate: data.startDate,
      // Stored with end-of-day time so the period stays open during the whole last day
      endDate: `${data.endDate}T23:59:59`,
      indexingDate: data.indexingDate,
      index: parseFloat(data.index),
      prices: data.prices
    };
    onSave(newIndexing);
    handleClose();
  };

  const handleClose = () => {
    reset(emptyForm);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(event, data) => !data.open && handleClose()}>
      <DialogSurface className="max-w-[600px] w-[90vw]">
        <DialogBody>
          <div className="flex justify-between items-start mb-4">
            <DialogTitle className="m-0 text-lg font-semibold">
              {editingIndexing ? "Redigera indexering" : "Skapa ny indexering"}
            </DialogTitle>
          </div>

          <DialogContent>
            <div className="flex flex-col gap-4">
              <Controller
                name="indexingName"
                control={control}
                rules={{ required: "Rubrik är obligatorisk" }}
                render={({ field }) => (
                  <Field
                    label="Rubrik:"
                    required
                    validationMessage={errors.indexingName?.message}
                  >
                    <Input {...field} placeholder="" />
                  </Field>
                )}
              />

              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <Field label="Beskrivning:" className="mb-10">
                    <ReactQuill
                      theme="snow"
                      className="rounded-quill-editor"
                      value={field.value || ""}
                      onChange={field.onChange}
                    />
                  </Field>
                )}
              />

              <Controller
                name="startDate"
                control={control}
                rules={{
                  required: "Öppnar är obligatorisk",
                  validate: (value) => {
                    const end = getValues("endDate");
                    return !end || value <= end || "Öppnar måste vara före eller samma dag som Stänger";
                  }
                }}
                render={({ field }) => (
                  <Field
                    label="Öppnar:"
                    required
                    validationMessage={errors.startDate?.message}
                  >
                    <Input {...field} type="date" onChange={(e) => { field.onChange(e); revalidateDates(); }} />
                  </Field>
                )}
              />

              <Controller
                name="endDate"
                control={control}
                rules={{
                  required: "Stänger är obligatorisk",
                  validate: (value) => {
                    const start = getValues("startDate");
                    return !start || value >= start || "Stänger måste vara efter eller samma dag som Öppnar";
                  }
                }}
                render={({ field }) => (
                  <Field
                    label="Stänger:"
                    required
                    validationMessage={errors.endDate?.message}
                  >
                    <Input {...field} type="date" onChange={(e) => { field.onChange(e); revalidateDates(); }} />
                  </Field>
                )}
              />

              <Controller
                name="indexingDate"
                control={control}
                rules={{
                  required: "Gäller från är obligatorisk",
                  validate: (value) => {
                    const end = getValues("endDate");
                    if (end && value <= end) return "Gäller från måste vara efter Stänger";
                    if (value <= getTodayKey()) return "Gäller från måste vara ett framtida datum";
                    return true;
                  }
                }}
                render={({ field }) => (
                  <Field
                    label="Gäller från:"
                    required
                    validationMessage={errors.indexingDate?.message}
                  >
                    <Input {...field} type="date" onChange={(e) => { field.onChange(e); revalidateDates(); }} />
                  </Field>
                )}
              />

              <Controller
                name="index"
                control={control}
                rules={{
                  required: "Index är obligatoriskt",
                  validate: (value) => parseFloat(value) > 0 || "Index måste vara större än 0"
                }}
                render={({ field }) => (
                  <Field
                    label="Index:"
                    required
                    validationMessage={errors.index?.message}
                    hint="Multipliceras med nuvarande pris, t.ex. 1.025000 = +2,5 %"
                  >
                    <Input 
                      type="number"
                      step="0.000001"
                      min="0"
                      {...field}
                    />
                  </Field>
                )}
              />

              <Controller
                name="prices"
                control={control}
                rules={{ validate: (value) => value.length > 0 || "Koppla minst en prisfråga" }}
                render={({ field }) => (
                  <Field
                    label="Koppla informationsmängd:"
                    required
                    validationMessage={errors.prices?.message}
                  >
                    <Dropdown
                      placeholder="Välj prisfrågor"
                      positioning={{ overflowBoundary: "window" }}
                      onOptionSelect={(_, data) => {
                        const selectedQuestions = prices.filter(p => 
                          data.selectedOptions.includes(p.questionID.toString())
                        );
                        field.onChange(selectedQuestions);
                      }}
                      selectedOptions={field.value && field.value.length > 0 ? field.value.map(p => p.questionID.toString()) : []}
                      multiselect
                    >
                      {prices.map((priceOption) => (
                        <Option key={priceOption.questionID} value={priceOption.questionID.toString()}>
                          {priceOption.heading}
                        </Option>
                      ))}

                    </Dropdown>
                  </Field>)}
              />
            </div>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose} size="large">
              Avbryt
            </Button>
            <Button
              onClick={handleSubmit(onSubmit)}
              appearance="primary"
              size="large"
              disabled={!isValid}
            >
              {editingIndexing ? "Spara" : "Skapa"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default CreateIndexing;
